import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/app/ui-bits";
import { Plus, PencilSimple, ArrowsClockwise, Bank, DeviceMobile, Wallet, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const TYPE_ICON = {
  bank: Bank, mobile_money: DeviceMobile, cash: Wallet, credit_card: Wallet, wallet: Wallet,
};
const TYPE_LABEL = {
  bank: "Bank", mobile_money: "Mobile money",
  cash: "Cash", credit_card: "Credit card", wallet: "Wallet",
};

export default function FinancialAccountsTab({ canEdit }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/finance/accounts");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      if (editing?.id) await api.patch(`/finance/accounts/${editing.id}`, payload);
      else await api.post("/finance/accounts", payload);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deactivate = async (a) => {
    if (!window.confirm(`Deactivate "${a.name}"?`)) return;
    try { await api.delete(`/finance/accounts/${a.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const seedRecon = async () => {
    setSeeding(true);
    try {
      const { data: r } = await api.post("/finance/accounts/seed-demo-recon");
      toast.success(`Demo reconciliation data created · ${r.created} records`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSeeding(false); }
  };

  if (!data) return <div className="font-mono-alt text-xs uppercase tracking-widest text-slate-400">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="financial-accounts-tab">
      {/* Totals strip */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {["bank", "mobile_money", "cash", "all"].map((k) => (
          <div key={k} className="dc-card p-3">
            <div className="overline">{k === "all" ? "Total across all accounts" : TYPE_LABEL[k]}</div>
            <div className="font-display text-xl font-bold text-slate-900 tabular-nums">{formatCurrency(data.totals[k] || 0)}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap justify-between gap-2">
        <div className="text-xs text-slate-500">Registry of company accounts · banks, mobile money (EVC+, E-Dahab), petty cash & wallets.</div>
        <div className="flex gap-2">
          {canEdit && (
            <Button variant="outline" size="sm" onClick={seedRecon} disabled={seeding} data-testid="seed-recon-btn">
              <ArrowsClockwise size={12} className={seeding ? "animate-spin mr-1" : "mr-1"} /> Generate demo reconciliation data
            </Button>
          )}
          {canEdit && (
            <Button size="sm" className="btn-primary-blue" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-account-btn">
              <Plus size={12} className="mr-1" /> New account
            </Button>
          )}
        </div>
      </div>

      {(data.items || []).length === 0 ? (
        <EmptyState icon={Bank} title="No accounts yet" description="Create a bank or mobile-money account to begin." />
      ) : (
        <div className="dc-card overflow-hidden">
          <div className="responsive-table-wrap">
            <table className="data-table w-full min-w-[720px]" data-testid="accounts-table">
              <thead>
                <tr>
                  <th>Account</th><th>Type</th><th>Provider</th><th>Account #</th>
                  <th className="text-right">Opening</th><th className="text-right">Current</th>
                  <th>Status</th>
                  {canEdit && <th className="text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => {
                  const Icon = TYPE_ICON[a.account_type] || Bank;
                  return (
                    <tr key={a.id} data-testid={`account-row-${a.id}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <Icon size={14} className="text-[#1E5CBA]" weight="fill" />
                          <div>
                            <div className="font-semibold text-slate-900">{a.name}</div>
                            {a.branch && <div className="text-[10px] text-slate-500">{a.branch}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3"><span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-600">{TYPE_LABEL[a.account_type]}</span></td>
                      <td className="px-5 py-3 text-sm text-slate-700">{a.provider}</td>
                      <td className="px-5 py-3 font-mono-alt text-xs">{a.account_number || "—"}</td>
                      <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{formatCurrency(a.opening_balance || 0)}</td>
                      <td className="px-5 py-3 text-right font-mono-alt tabular-nums font-semibold">{formatCurrency(a.current_balance || 0)}</td>
                      <td className="px-5 py-3"><StatusBadge status={a.is_active ? "active" : "archived"} /></td>
                      {canEdit && (
                        <td className="px-5 py-3 text-right">
                          <div className="inline-flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => { setEditing(a); setOpen(true); }} data-testid={`account-edit-${a.id}`}><PencilSimple size={10} /></Button>
                            {a.is_active && <Button size="sm" variant="outline" onClick={() => deactivate(a)} data-testid={`account-deactivate-${a.id}`}><Trash size={10} className="text-red-600" /></Button>}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AccountDialog open={open} onClose={() => { setOpen(false); setEditing(null); }} editing={editing} onSave={save} accountTypes={data.account_types || ["bank", "mobile_money", "cash"]} />
    </div>
  );
}

function AccountDialog({ open, onClose, editing, onSave, accountTypes }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (open) setForm(editing ? { ...editing } : {
      name: "", account_type: "bank", provider: "", currency: "USD",
      account_number: "", branch: "",
      opening_balance: 0, current_balance: 0, is_active: true, notes: "",
    });
  }, [open, editing]);
  if (!form) return null;

  const submit = () => {
    if (!form.name || !form.provider) return toast.error("Name and provider required");
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="account-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Edit" : "New"} financial account</DialogTitle>
          <DialogDescription>Banks, mobile money, petty cash or any other account used by Descon.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Account name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{accountTypes.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t] || t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
          </div>
          <div><Label>Provider *</Label><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="Premier Bank / EVC+ / …" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Account # / wallet ID</Label><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} /></div>
            <div><Label>Branch / location</Label><Input value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Opening balance</Label><Input type="number" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: parseFloat(e.target.value || 0) })} /></div>
            <div><Label>Current balance</Label><Input type="number" value={form.current_balance} onChange={(e) => setForm({ ...form, current_balance: parseFloat(e.target.value || 0) })} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: !!v })} />
            Active
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="btn-primary-blue" data-testid="account-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
